import { DoubleDeckerCell } from '@plato/ui-legacy/components/list-views/double-decker-cell';
import { DataText } from '@plato/ui-legacy/components/typography/data-text';
import { NumberOrEmpty } from '@plato/ui-legacy/components/typography/number-or-empty';
import { Checkbox } from '@plato/ui-legacy/components/ui/checkbox';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import type { ColumnDef } from '@plato/ui/components/data-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { GetProductAvailibilityByLocationListItem } from '../../../server/types/infer-trpc-types.ts';

export function useProductAvailibilityColumns(includeSelect = false) {
    const { t } = useTranslation();
    const locale = useResolvedLocale();

    const productAvailibilityColumns: ColumnDef<string, GetProductAvailibilityByLocationListItem>[] =
        useMemo(() => {
            const columns: ColumnDef<string, GetProductAvailibilityByLocationListItem>[] = [];

            if (includeSelect) {
                columns.push({
                    id: 'select',
                    cell: ({ row }) => (
                        <Checkbox
                            checked={row.getIsSelected()}
                            onCheckedChange={
                                row.getIsSelected()
                                    ? undefined
                                    : (value) => {
                                          row.toggleSelected(!!value);
                                      }
                            }
                            aria-label={t('aria__select_row')}
                        />
                    ),
                    enableSorting: false,
                    size: 30,
                });
            }

            columns.push(
                {
                    accessorKey: 'storage_location_id',
                    header: t('table_header__location', {
                        ns: 'table-product-availibity',
                    }),
                    cell: ({ cell }) => (
                        <DoubleDeckerCell
                            align="left"
                            topLevel={cell.getValue() as string}
                            bottomLevel={cell.row.original.storage_location_name}
                        />
                    ),
                    size: 150,
                },
                {
                    accessorKey: 'quantity_unit_sales',
                    header: t('table_header__unit', {
                        ns: 'table-product-availibity',
                    }),
                    size: 40,
                },
                {
                    accessorKey: 'stock_quantity',
                    header: t('table_header__inventory', {
                        ns: 'table-product-availibity',
                    }),
                    cell: ({ cell }) => (
                        <DataText>
                            <NumberOrEmpty>{cell.getValue() as number}</NumberOrEmpty>
                        </DataText>
                    ),
                },
                {
                    accessorKey: 'reserved',
                    header: t('table_header__reserved', {
                        ns: 'table-product-availibity',
                    }),
                    cell: ({ cell }) => (
                        <DataText>
                            <NumberOrEmpty>{cell.getValue() as number}</NumberOrEmpty>
                        </DataText>
                    ),
                },

                {
                    accessorKey: 'picked',
                    header: t('table_header__picked', {
                        ns: 'table-product-availibity',
                    }),
                    cell: ({ cell }) => (
                        <DataText>
                            <NumberOrEmpty>{cell.getValue() as number}</NumberOrEmpty>
                        </DataText>
                    ),
                },

                {
                    accessorKey: 'effective',
                    header: t('table_header__effective_inventory', {
                        ns: 'table-product-availibity',
                    }),
                    cell: ({ cell }) => (
                        <DataText>
                            <NumberOrEmpty>{cell.getValue() as number}</NumberOrEmpty>
                        </DataText>
                    ),
                },
            );

            return columns;
        }, [t, locale, includeSelect]);

    return productAvailibilityColumns;
}
